/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, type ComponentProps } from 'react';

import { useQuery } from '@apollo/client/react';
import { useMsal } from '@azure/msal-react';
import {
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Autocomplete,
  Container,
  FormGroup,
  Checkbox,
  Switch,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { orderBy } from 'lodash';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { allTimezones, useTimezoneSelect } from 'react-timezone-select';

import type { Event } from '../../../../Data/types';
import { GET_AUDIENCE_NAMES, GET_EVENT_FORMAT, GET_OFFICES, GET_OPTION_VALUES } from '../../../../graphql/queries';
import useCreateEvent from '../../../../hooks/useCreateEvent/useCreateEvent';
import useUpdateEvent from '../../../../hooks/useUpdateEvent/useUpdateEvent';
import useUpdateEventInASeries from '../../../../hooks/useUpdateEventInASeries/useUpdateEventInASeries';
import {
  eventPaneLoadingAtom,
  formModeAtom,
  isSeriesEventActionAtom,
  notificationMessageAtom,
  selectedEventAtom,
  showEventPaneAtom,
} from '../../../../state/atoms/atoms';
import { DataGridField } from '../../../shared/DataGridField/DataGridField';
import DropdownSelect from '../../../shared/DropdownSelect/DropdownSelect';
import { Field } from '../../../shared/Field/Field';
import { MLPersona } from '../../../shared/MLPersona/MLPersona';
import AutocompleteInput from '../FormInputs/AutocompleteInput/AutocompleteInput';
import { DateTimePicker } from '../FormInputs/DateTimePicker/DateTimePicker';
import OutsideSpeakers from '../FormInputs/OutsideSpeakers/OutsideSpeakers';
import PeoplePicker from '../FormInputs/PeoplePicker/PeoplePicker';
import RecurrenceInput from '../FormInputs/Recurrence/RecurrenceInput/RecurrenceInput';
import RichTextField from '../FormInputs/RichText/RichTextField/RichTextField';
import { TimezonePicker } from '../FormInputs/TimezonePicker/TimezonePicker';

export const EventForm = () => {
  type FormSubmitEvent = Parameters<NonNullable<ComponentProps<'form'>['onSubmit']>>[0];

  const { parseTimezone } = useTimezoneSelect({
    labelStyle: 'original',
    timezones: allTimezones,
  });
  const { data: OptionValues } = useQuery(GET_OPTION_VALUES);
  const [formMode] = useAtom(formModeAtom);
  const [selectedEvent, setSelectedEvent] = useAtom(selectedEventAtom);
  const [loading, setLoading] = useAtom(eventPaneLoadingAtom);
  const {
    formState: { errors },
    handleSubmit,
    watch,
    control,
    setValue,
  } = useForm<Event>({ values: selectedEvent ?? undefined });
  const watchIsAllDay = watch('allDay');
  const watchIsRecurring = watch('isRecurring');
  const watchHostingType = watch('hostingType');
  const watchEventType = watch('eventType');
  const watchFirmInitiatives = watch('firmInitiatives');
  const watchIsLocationExternal = watch('isLocationExternal');
  const watchIsOutsideSpeaker = watch('isOutsideSpeaker');
  const { createEvent } = useCreateEvent();
  const { updateEvent } = useUpdateEvent();
  const { updateEventInASeries } = useUpdateEventInASeries();
  const { instance } = useMsal();
  const currentUser = instance?.getAllAccounts()?.[0]?.idTokenClaims?.['onpremisesuserprincipalname'] as string;
  const [, setNotificationMessage] = useAtom(notificationMessageAtom);
  const [, setshowEventPane] = useAtom(showEventPaneAtom);
  const [isSeriesEventAction] = useAtom(isSeriesEventActionAtom);
  const [isRecurrenceValid, setIsRecurrenceValid] = useState(true);

  const onSubmit: SubmitHandler<Event> = async (data: Event) => {
    setLoading(true);
    if (formMode === 'new') {
      await createEvent(data, currentUser)
        .then(() => {
          setshowEventPane(false);
          setLoading(false);
          setNotificationMessage({
            messageTitle: 'Event Created',
            intent: 'success',
            messageBody: 'Event has been created successfully',
          });
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
          setNotificationMessage({
            messageTitle: 'Event Creation Failed',
            intent: 'error',
            messageBody: 'Event creation failed. Please try again',
          });
        });
    }
    if (formMode === 'edit') {
      if (!isSeriesEventAction && selectedEvent?.isRecurring) {
        await updateEventInASeries(selectedEvent.id, selectedEvent.eventDateTime!.start!, currentUser, data)
          .then(() => {
            setshowEventPane(false);
            setLoading(false);
            setSelectedEvent(null);
            setNotificationMessage({
              messageTitle: 'Event Updated',
              intent: 'success',
              messageBody: 'Event has been updated successfully',
            });
          })
          .catch((error) => {
            console.error(error);
            setNotificationMessage({
              messageTitle: 'Event Update Failed',
              intent: 'error',
              messageBody: 'Event update failed. Please try again',
            });
          });
        setLoading(false);
        return;
      }
      await updateEvent(data, currentUser)
        .then(() => {
          setshowEventPane(false);
          setLoading(false);
          setSelectedEvent(null);
          setNotificationMessage({
            messageTitle: 'Event Updated',
            intent: 'success',
            messageBody: 'Event has been updated successfully',
          });
        })
        .catch((error) => {
          console.error(error);
          setNotificationMessage({
            messageTitle: 'Event Update Failed',
            intent: 'error',
            messageBody: 'Event update failed. Please try again',
          });
        });
    }
  };

  useEffect(() => {
    if (selectedEvent && formMode === 'edit' && !isSeriesEventAction) {
      setValue('isRecurring', isSeriesEventAction);
    }
  }, [selectedEvent, formMode, isSeriesEventAction]);

  useEffect(() => {
    if (!watchIsRecurring) setValue('recurrence', null);
  }, [watchIsRecurring]);

  useEffect(() => {
    if (watchIsOutsideSpeaker) {
      setValue('mlSpeaker', null);
    } else {
      setValue('outsideSpeakers', null);
    }
  }, [watchIsOutsideSpeaker]);

  useEffect(() => {
    if (watchHostingType && formMode !== 'preview') setValue('eventType', null);
  }, [watchHostingType]);

  useEffect(() => {
    if (watchIsLocationExternal) {
      setValue('officeLocation', null);
    } else {
      setValue('externalLocation', '');
    }
  }, [watchIsLocationExternal]);

  const onFormSubmit = (event: FormSubmitEvent) => {
    void handleSubmit(onSubmit, console.error)(event);
  };

  return (
    <Container sx={{ padding: '1rem' }}>
      <form id="event-form" onSubmit={onFormSubmit} noValidate>
        <Stack spacing={1}>
          <Controller
            name="title"
            control={control}
            defaultValue=""
            rules={{
              required: 'Event Name is required',
            }}
            render={({ field: { onChange, value } }) => (
              <Field
                required
                label="Event Name"
                tooltip="Enter event name"
                showValidationMessageIcon
                validationState={errors.title ? 'error' : 'none'}
                validationMessage={errors.title?.message?.toString()}
                fullWidth
              >
                <TextField
                  fullWidth
                  slotProps={{
                    htmlInput: { readOnly: formMode === 'preview' },
                  }}
                  size="small"
                  error={Boolean(errors.title)}
                  placeholder={formMode === 'preview' ? '' : 'Enter event name'}
                  value={value}
                  onChange={onChange}
                />
              </Field>
            )}
          />
          <Controller
            name="description"
            control={control}
            defaultValue="<p></p><p></p><p></p>"
            render={({ field: { onChange, value } }) => (
              <RichTextField
                label="Description"
                tooltip="Enter event description"
                value={value}
                onChange={onChange}
                formMode={formMode}
                sx={{
                  overflowY: 'scroll',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              />
            )}
          />
          <Divider>Event Date & Time</Divider>
          <Field label="All Day / Recurring" tooltip="Select if event is all day or recurring" fullWidth>
            <FormGroup row>
              <Controller
                name="allDay"
                control={control}
                defaultValue={false}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    labelPlacement="end"
                    label="All Day"
                    disabled={formMode === 'preview'}
                    control={
                      <Checkbox
                        slotProps={{
                          root: { name: 'allDay' },
                        }}
                        checked={value}
                        onChange={onChange}
                        size="small"
                      />
                    }
                  />
                )}
              />
              <Controller
                name="isRecurring"
                control={control}
                defaultValue={false}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    label="Recurring"
                    labelPlacement="end"
                    disabled={
                      (selectedEvent && selectedEvent.isRecurring && !isSeriesEventAction) || formMode === 'preview'
                    }
                    control={
                      <Checkbox
                        checked={value}
                        slotProps={{
                          root: { name: 'isRecurring' },
                        }}
                        disabled={
                          (selectedEvent && selectedEvent.isRecurring && !isSeriesEventAction) || formMode === 'preview'
                        }
                        onChange={onChange}
                        size="small"
                      />
                    }
                  />
                )}
              />
            </FormGroup>
          </Field>
          <Controller
            name="timezone"
            control={control}
            defaultValue={parseTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone).value}
            rules={{
              required: 'Time Zone is required',
            }}
            render={({ field: { onChange, value } }) => (
              <TimezonePicker
                label="Time Zone"
                value={value ?? null}
                onChange={onChange}
                required={!watchIsAllDay}
                readOnly={formMode === 'preview'}
                disabled={watchIsAllDay}
                tooltip="Select the time zone for the event"
              />
            )}
          />
          {(!watchIsRecurring || formMode === 'preview') && (
            <Controller
              name="eventDateTime"
              control={control}
              rules={{
                required: 'Start Date / Time is required',
                validate: (value) => {
                  if (!value || (!value.start && !value.end)) {
                    return 'Start Date / Time is required';
                  } else if (value.start && value.end && value.start > value.end) {
                    return 'Start Date / Time should be before End Date / Time';
                  }
                  return true;
                },
              }}
              defaultValue={null}
              render={({ field: { onChange, value } }) => (
                <DateTimePicker
                  label="Event Date / Time"
                  startLabel="Event Start"
                  endLabel="Event End"
                  isAllDay={watchIsAllDay}
                  value={value ?? null}
                  onChange={onChange}
                  errorMessage={errors.eventDateTime?.message?.toString()}
                  tooltip="Select start and end date and time for the event"
                  required
                  readOnly={formMode === 'preview'}
                  isRecurring={watchIsRecurring}
                />
              )}
            />
          )}

          {watchIsRecurring && (
            <Controller
              name="recurrence"
              control={control}
              defaultValue={null}
              shouldUnregister={!watchIsRecurring}
              rules={{
                required: 'Recurrence Pattern is required',
                validate: {
                  validTimeRange: () => isRecurrenceValid || 'Invalid time range: End time must be after start time',
                },
              }}
              render={({ field: { onChange, value } }) => {
                return (
                  <RecurrenceInput
                    value={value}
                    onChange={onChange}
                    isAllDay={watchIsAllDay}
                    readOnly={formMode === 'preview'}
                    onValidationChange={setIsRecurrenceValid}
                  />
                );
              }}
            />
          )}
          <Divider>Event Type</Divider>
          <Controller
            name="hostingType"
            control={control}
            defaultValue="internal"
            rules={{
              required: 'External or Internal is required',
            }}
            render={({ field: { onChange, value } }) => (
              <Field
                required
                label="Event Category"
                tooltip="Select Internal, External, or 3rd Party event"
                fullWidth
                validationState={errors.hostingType ? 'error' : 'none'}
                validationMessage={errors.hostingType?.message?.toString()}
                showValidationMessageIcon
                size="small"
              >
                <Select
                  labelId="event-category-label"
                  value={value ?? ''}
                  onChange={onChange}
                  {...{ disabled: formMode === 'preview' }}
                >
                  <MenuItem value="internal">Internal</MenuItem>
                  <MenuItem value="external">External</MenuItem>
                  <MenuItem value="3rd party">3rd Party</MenuItem>
                </Select>
              </Field>
            )}
          />
          {watchHostingType !== '3rd party' && (
            <Controller
              name="eventType"
              control={control}
              defaultValue={null}
              rules={{
                required: 'Event Type is required',
              }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Field
                    required
                    label="Event Type"
                    tooltip="Select an event type"
                    validationState={errors.eventType ? 'error' : 'none'}
                    validationMessage={errors.eventType?.message?.toString()}
                    showValidationMessageIcon
                    fullWidth
                  >
                    <Autocomplete
                      options={
                        !watchHostingType || watchHostingType === 'internal'
                          ? orderBy(
                              OptionValues?.gec_event_type.filter(
                                (type) => type.hosting_type === 'internal' || type.hosting_type === 'all',
                              ),
                              [
                                (type) => (type.event_type === 'Other' ? 1 : 0),
                                (type) => type.event_type.toLowerCase(),
                              ],
                              ['asc', 'asc'],
                            )
                          : orderBy(
                              OptionValues?.gec_event_type.filter(
                                (type) => type.hosting_type === 'external' || type.hosting_type === 'all',
                              ),
                              [
                                (type) => (type.event_type === 'Other' ? 1 : 0),
                                (type) => type.event_type.toLowerCase(),
                              ],
                              ['asc', 'asc'],
                            )
                      }
                      getOptionLabel={(option) => option.event_type}
                      value={value}
                      onChange={(_, newValue) => {
                        onChange(newValue);
                      }}
                      renderInput={(params) => (
                        <AutocompleteInput
                          error={Boolean(errors.eventType)}
                          params={params}
                          placeholder={formMode === 'preview' ? '' : 'Select an event type'}
                        />
                      )}
                      readOnly={formMode === 'preview'}
                    />
                  </Field>
                );
              }}
            />
          )}
          {watchEventType?.id === 5 && watchHostingType !== '3rd party' && (
            <Controller
              name="eventTypeOther"
              control={control}
              defaultValue=""
              rules={{
                required: 'Other is required',
              }}
              render={({ field: { onChange, value } }) => (
                <Field
                  required
                  fullWidth
                  label="Event Type Other"
                  tooltip="Enter other event type not specified in the list"
                  validationState={errors.eventTypeOther ? 'error' : 'none'}
                  validationMessage={errors.eventTypeOther?.message?.toString()}
                  showValidationMessageIcon
                >
                  <TextField
                    fullWidth
                    size="small"
                    error={Boolean(errors.eventTypeOther)}
                    slotProps={{
                      htmlInput: { readOnly: formMode === 'preview' },
                    }}
                    value={value}
                    onChange={onChange}
                    placeholder={formMode === 'preview' ? '' : 'Other Event Type'}
                  />
                </Field>
              )}
            />
          )}

          {watchHostingType === '3rd party' && (
            <Controller
              name="sponsorOrganization"
              control={control}
              defaultValue=""
              rules={{
                required: 'Sponsoring Organization is required',
              }}
              render={({ field: { onChange, value } }) => (
                <Field
                  required={true}
                  label="Sponsoring Organization"
                  tooltip="Enter sponsoring organization"
                  validationState={errors.sponsorOrganization ? 'error' : 'none'}
                  validationMessage={errors.sponsorOrganization?.message?.toString()}
                  showValidationMessageIcon
                  fullWidth
                >
                  <TextField
                    error={Boolean(errors.sponsorOrganization)}
                    slotProps={{
                      htmlInput: { readOnly: formMode === 'preview' },
                    }}
                    fullWidth
                    value={value}
                    onChange={onChange}
                    placeholder={formMode === 'preview' ? '' : 'Sponsoring Organization'}
                    size="small"
                  />
                </Field>
              )}
            />
          )}
          {watchHostingType === 'external' && (
            <Controller
              name="sponsorOrganization"
              control={control}
              defaultValue=""
              rules={{
                required:
                  watchHostingType === 'external' &&
                  (watchEventType?.event_type === 'PG/Industry/Initiative Sponsorship' ||
                    watchEventType?.event_type === 'PG/Industry/Initiative Speaking Engagement')
                    ? 'Sponsoring Organization is required'
                    : false,
              }}
              render={({ field: { onChange, value } }) => (
                <Field
                  required={
                    watchHostingType === 'external' &&
                    (watchEventType?.event_type === 'PG/Industry/Initiative Sponsorship' ||
                      watchEventType?.event_type === 'PG/Industry/Initiative Speaking Engagement')
                  }
                  label="Sponsoring Organization"
                  tooltip="Enter sponsoring organization"
                  validationState={errors.sponsorOrganization ? 'error' : 'none'}
                  validationMessage={errors.sponsorOrganization?.message?.toString()}
                  showValidationMessageIcon
                  fullWidth
                >
                  <TextField
                    error={Boolean(errors.sponsorOrganization)}
                    slotProps={{
                      htmlInput: { readOnly: formMode === 'preview' },
                    }}
                    fullWidth
                    value={value}
                    onChange={onChange}
                    placeholder={formMode === 'preview' ? '' : 'Sponsoring Organization'}
                    size="small"
                  />
                </Field>
              )}
            />
          )}
          <Controller
            name="cle"
            control={control}
            defaultValue={false}
            render={({ field: { onChange, value } }) => (
              <Field label="CLE" tooltip="Check if event is CLE" orientation="horizontal" fullWidth>
                <Switch
                  {...{
                    checked: value,
                    onChange: (event: any, checked: any) => onChange(event, checked),
                  }}
                  disabled={formMode === 'preview'}
                  size="small"
                />
              </Field>
            )}
          />
          <Divider>Host PG, Department, Industry, and/or Initiative</Divider>
          <Controller
            name="practiceGroups"
            control={control}
            defaultValue={[]}
            render={({ field: { onChange, value } }) => (
              <Field label="a. Practice Group(s) (Select all that apply)" tooltip="Select practice group(s)" fullWidth>
                <DropdownSelect
                  multiple
                  limitTags={2}
                  optionValueKey="gec_practice_groups_rollup"
                  placeholder={formMode === 'preview' ? '' : 'Select Practice Group(s)'}
                  value={value}
                  onChange={onChange}
                  query={GET_OPTION_VALUES}
                  readOnly={formMode === 'preview'}
                />
              </Field>
            )}
          />
          <Controller
            name="departments"
            control={control}
            defaultValue={[]}
            render={({ field: { onChange, value } }) => (
              <Field
                label="b. Firm Department(s) (Select all that apply)"
                tooltip="Select firm department(s)"
                fullWidth
              >
                <DropdownSelect
                  multiple
                  limitTags={2}
                  optionValueKey="gec_departments"
                  placeholder={formMode === 'preview' ? '' : 'Select Firm Department(s)'}
                  value={value}
                  onChange={onChange}
                  query={GET_OPTION_VALUES}
                  readOnly={formMode === 'preview'}
                />
              </Field>
            )}
          />
          <Controller
            name="industries"
            control={control}
            defaultValue={[]}
            render={({ field: { onChange, value } }) => (
              <Field
                label="c. Industry(ies), Strategic Initiative(s) & Task Force(s) (Select all that apply)"
                tooltip="Select industry(ies), strategic initiative(s) & task force(s)"
                fullWidth
              >
                <DropdownSelect
                  multiple
                  limitTags={2}
                  optionValueKey="gec_industries"
                  placeholder={
                    formMode === 'preview' ? '' : 'Select Industry(ies), Strategic Initiative(s) & Task Force(s)'
                  }
                  value={value}
                  onChange={onChange}
                  query={GET_OPTION_VALUES}
                  readOnly={formMode === 'preview'}
                />
              </Field>
            )}
          />
          <Controller
            name="firmInitiatives"
            control={control}
            defaultValue={[]}
            render={({ field: { onChange, value } }) => (
              <Field
                label="d. Firm Initiative(s) (Select all that apply)"
                tooltip="Select firm initiative(s)"
                fullWidth
              >
                <DropdownSelect
                  multiple
                  limitTags={2}
                  optionValueKey="gec_firm_initiatives"
                  placeholder={formMode === 'preview' ? '' : 'Select Firm Initiative(s)'}
                  value={value}
                  onChange={onChange}
                  query={GET_OPTION_VALUES}
                  readOnly={formMode === 'preview'}
                />
              </Field>
            )}
          />
          {watchFirmInitiatives?.find((initiative) => initiative.id === 4) && (
            <Controller
              name="hostingGroupOther"
              control={control}
              defaultValue=""
              rules={{
                required: 'Other Firm Initiatives is required',
              }}
              render={({ field: { onChange, value } }) => (
                <Field
                  required
                  fullWidth
                  label="Other Firm Initiatives"
                  tooltip="Enter other firm initiatives not specified in the list"
                  validationState={errors.hostingGroupOther ? 'error' : 'none'}
                  validationMessage={errors.hostingGroupOther?.message?.toString()}
                  showValidationMessageIcon
                >
                  <TextField
                    fullWidth
                    error={Boolean(errors.hostingGroupOther)}
                    slotProps={{
                      htmlInput: { readOnly: formMode === 'preview' },
                    }}
                    value={value}
                    onChange={onChange}
                    placeholder={formMode === 'preview' ? '' : 'Other Firm Initiatives'}
                    size="small"
                  />
                </Field>
              )}
            />
          )}
          <Divider>Event Location</Divider>
          <Controller
            name="eventFormat"
            control={control}
            defaultValue={null}
            rules={{
              required: 'Event Format is required',
            }}
            render={({ field: { onChange, value } }) => (
              <Field
                required
                label="Event Format"
                tooltip="Select event format"
                fullWidth
                validationState={errors.eventFormat ? 'error' : 'none'}
                validationMessage={errors.eventFormat?.message?.toString()}
                showValidationMessageIcon
              >
                <DropdownSelect
                  value={typeof value === 'object' || value == null ? value : null}
                  onChange={onChange}
                  query={GET_EVENT_FORMAT}
                  placeholder={formMode === 'preview' ? '' : 'Select Event Format'}
                  optionValueKey="gec_event_format"
                  readOnly={formMode === 'preview'}
                  error={Boolean(errors.eventFormat)}
                />
              </Field>
            )}
          />
          <Field fullWidth label="Location Type" tooltip="Check if location is external">
            <FormGroup row>
              <Controller
                name="isLocationExternal"
                control={control}
                defaultValue={false}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    labelPlacement="end"
                    label="External Location"
                    disabled={formMode === 'preview'}
                    control={
                      <Checkbox
                        disabled={formMode === 'preview'}
                        checked={value}
                        onChange={onChange}
                        size="small"
                        slotProps={{
                          root: { name: 'isLocationExternal' },
                        }}
                      />
                    }
                  />
                )}
              />
            </FormGroup>
          </Field>
          {watchIsLocationExternal && (
            <Controller
              name="externalLocation"
              control={control}
              defaultValue=""
              rules={{
                required: 'Outside venue is required',
              }}
              render={({ field: { onChange, value } }) => (
                <Field
                  required
                  label="Outside Venue"
                  tooltip="Enter outside venue incl. City, State and Country"
                  validationState={errors.externalLocation ? 'error' : 'none'}
                  validationMessage={errors.externalLocation?.message?.toString()}
                  showValidationMessageIcon
                  fullWidth
                >
                  <TextField
                    fullWidth
                    error={Boolean(errors.externalLocation)}
                    slotProps={{
                      htmlInput: { readOnly: formMode === 'preview' },
                    }}
                    size="small"
                    value={value}
                    onChange={onChange}
                    placeholder={formMode === 'preview' ? '' : 'Outside venue'}
                  />
                </Field>
              )}
            />
          )}
          {!watchIsLocationExternal && (
            <Controller
              name="officeLocation"
              control={control}
              defaultValue={[]}
              rules={{
                required: 'Office Location is required',
              }}
              render={({ field: { onChange, value } }) => (
                <Field
                  required
                  label="Office Location"
                  tooltip="Select office location"
                  fullWidth
                  validationState={errors.officeLocation ? 'error' : 'none'}
                  validationMessage={errors.officeLocation?.message?.toString()}
                  showValidationMessageIcon
                >
                  <DropdownSelect
                    multiple
                    limitTags={2}
                    value={value ?? []}
                    onChange={onChange}
                    query={GET_OFFICES}
                    placeholder={formMode === 'preview' ? '' : 'Select Location'}
                    optionValueKey="gec_offices"
                    readOnly={formMode === 'preview'}
                    error={Boolean(errors.officeLocation)}
                  />
                </Field>
              )}
            />
          )}
          <Divider>Other Event Details</Divider>
          <Field fullWidth label="Attendance Type" tooltip="Check if event is invite only">
            <FormGroup row>
              <Controller
                name="inviteOnly"
                control={control}
                defaultValue={false}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    label="Invite Only"
                    labelPlacement="end"
                    disabled={formMode === 'preview'}
                    control={
                      <Checkbox
                        checked={value}
                        disabled={formMode === 'preview'}
                        onChange={onChange}
                        size="small"
                        slotProps={{
                          root: { name: 'inviteOnly' },
                        }}
                      />
                    }
                  />
                )}
              />
            </FormGroup>
          </Field>
          <Controller
            name="officeVisibility"
            control={control}
            defaultValue={[]}
            rules={{
              required: 'Calendar Visibility is required',
            }}
            render={({ field: { onChange, value } }) => (
              <Field
                required
                label="Calendar Visibility"
                tooltip="Select the calendars that you wish this event to show on (select all that apply)"
                fullWidth
                validationState={errors.officeVisibility ? 'error' : 'none'}
                validationMessage={errors.officeVisibility?.message?.toString()}
                showValidationMessageIcon
              >
                <DropdownSelect
                  multiple
                  value={value ?? []}
                  onChange={onChange}
                  query={GET_OFFICES}
                  placeholder={formMode === 'preview' ? '' : 'Select calendar(s)'}
                  optionValueKey="gec_offices"
                  readOnly={formMode === 'preview'}
                  error={Boolean(errors.officeVisibility)}
                />
              </Field>
            )}
          />
          <Controller
            name="eventContact"
            control={control}
            defaultValue={null}
            rules={{
              required: 'Event Contact is required',
            }}
            render={({ field: { onChange, value } }) => (
              <Field
                required
                label="Event Contact"
                tooltip="Select event contact"
                fullWidth
                validationState={errors.eventContact ? 'error' : 'none'}
                validationMessage={errors.eventContact?.message?.toString()}
                showValidationMessageIcon
              >
                <PeoplePicker
                  onChange={onChange}
                  error={Boolean(errors.eventContact)}
                  value={value}
                  required
                  readOnly={formMode === 'preview'}
                />
              </Field>
            )}
          />
          <Field label="Sponsoring Attorney" tooltip="Select sponsoring attorney" fullWidth>
            <Controller
              name="sponsoringAttorney"
              control={control}
              defaultValue={null}
              render={({ field: { onChange, value } }) => (
                <PeoplePicker onChange={onChange} value={value} readOnly={formMode === 'preview'} />
              )}
            />
          </Field>
          <Controller
            name="audience"
            control={control}
            defaultValue={[]}
            render={({ field: { onChange, value } }) => (
              <Field fullWidth label="Audience" tooltip="Select audience">
                <DropdownSelect
                  value={value}
                  onChange={onChange}
                  query={GET_AUDIENCE_NAMES}
                  placeholder={formMode === 'preview' ? '' : 'Select Audience'}
                  optionValueKey="gec_audience"
                  readOnly={formMode === 'preview'}
                  multiple
                />
              </Field>
            )}
          />
          <Divider>Event Speaker(s)</Divider>
          <Controller
            name="isOutsideSpeaker"
            control={control}
            defaultValue={false}
            render={({ field: { onChange, value } }) => (
              <Field
                label="Outside Speaker"
                tooltip="Check if event has an outside speaker"
                orientation="horizontal"
                fullWidth
              >
                <Switch
                  {...{
                    checked: value,
                    onChange: (event: any, checked: any) => onChange(event, checked),
                  }}
                  disabled={formMode === 'preview'}
                  size="small"
                />
              </Field>
            )}
          />
          {watchIsOutsideSpeaker && (
            <Controller
              name="outsideSpeakers"
              control={control}
              defaultValue={[]}
              rules={{
                required: 'Outside Speaker(s) is required at least one',
              }}
              render={({ field: { onChange, value } }) => (
                <DataGridField
                  label="List of Outside Speaker(s)"
                  tooltip="Add outside speaker(s)"
                  fullWidth
                  required
                  validationState={errors.outsideSpeakers ? 'error' : 'none'}
                  validationMessage={errors.outsideSpeakers?.message?.toString()}
                  showValidationMessageIcon
                >
                  <OutsideSpeakers
                    value={value ?? []}
                    readonly={formMode === 'preview'}
                    onChange={onChange}
                    error={Boolean(errors.outsideSpeakers)}
                  />
                </DataGridField>
              )}
            />
          )}
          {!watchIsOutsideSpeaker && (
            <Controller
              name="mlSpeaker"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Field fullWidth label="Morgan Lewis Speaker(s)" tooltip="Select Morgan Lewis speaker(s)">
                  <PeoplePicker value={value} onChange={onChange} readOnly={formMode === 'preview'} />
                </Field>
              )}
            />
          )}
          {formMode !== 'new' && (
            <>
              <Divider>System Information</Divider>
              <Box
                sx={{
                  backgroundColor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Stack direction="row" spacing={2}>
                  <Field label="Created By" tooltip="User who created this event" fullWidth>
                    <Controller
                      name="created_by_user"
                      control={control}
                      disabled={true}
                      render={({ field: { value } }) => (
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'nowrap' }}>
                          <MLPersona user={value ?? undefined} />
                          <Typography variant="body2" noWrap>
                            {value?.DisplayName ?? 'Unknown'}
                          </Typography>
                        </Stack>
                      )}
                    />
                  </Field>
                  <Field label="Created At" tooltip="Date and time event was created" fullWidth>
                    <Typography variant="body2" sx={{ py: 0.5 }}>
                      {selectedEvent?.created_at ? dayjs(selectedEvent.created_at).format('MM/DD/YYYY hh:mm A') : '—'}
                    </Typography>
                  </Field>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Field label="Modified By" tooltip="User who last modified this event" fullWidth>
                    <Controller
                      name="updated_by_user"
                      control={control}
                      disabled={true}
                      render={({ field: { value } }) => (
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'nowrap' }}>
                          <MLPersona user={value ?? undefined} />
                          <Typography variant="body2" noWrap>
                            {value?.DisplayName ?? 'Unknown'}
                          </Typography>
                        </Stack>
                      )}
                    />
                  </Field>
                  <Field label="Modified At" tooltip="Date and time event was last modified" fullWidth>
                    <Typography variant="body2" sx={{ py: 0.5 }}>
                      {selectedEvent?.updated_at ? dayjs(selectedEvent.updated_at).format('MM/DD/YYYY hh:mm A') : '—'}
                    </Typography>
                  </Field>
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </form>
      <Backdrop
        open={loading}
        sx={{
          position: 'absolute',
          inset: 0,
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};
